import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { SharedModule } from '@shared/shared.module';
import { ConsolidatedViewComponent } from './components/consolidated-view/consolidated-view.component';
import { DataRoomDocumentsStore } from './components/data-room-documents/data-room-documents.store';
import { DocumentsSectionComponent } from './components/documents-section/documents-section.component';
import { PartnerDetailComponent } from './components/partner-detail/partner-detail.component';
import { PartnerSelectorComponent } from './components/partner-selector/partner-selector.component';
import { PartnerIncomeTaxSectionState } from './partner-income-tax-section.state';
import { PartnerIncomeTaxStateService } from './partner-income-tax-state.service';
import {
  PARAMETRIZATION_ROUTE,
  PartnerIncomeTaxFolderLink,
  buildFolderLink,
} from './partner-income-tax.constants';
import { PartnerIncomeTaxReadingClock } from './partner-income-tax-reading-clock';
import { describeBaseRange } from './utils/partner-income-tax-format.util';
import { describeReadingProgress } from './utils/reading-progress.util';

/**
 * Aba "IR dos sócios" da Análise Econômica (card 3515), no layout do protótipo 01: linha de contexto, pílulas dos
 * sócios e a visão do sócio ou do consolidado (herói, KPIs, pontos e abas internas). Estado no
 * `PartnerIncomeTaxStateService`, provido pelo `DfpComponent`; a seção interna (`?section=`) e a lista de documentos
 * do Data Room são providas aqui. Ver o README desta pasta.
 */
@Component({
  selector: 'app-partner-income-tax',
  standalone: true,
  imports: [
    SharedModule,
    RouterLink,
    PartnerSelectorComponent,
    PartnerDetailComponent,
    ConsolidatedViewComponent,
    DocumentsSectionComponent,
  ],
  providers: [PartnerIncomeTaxSectionState, DataRoomDocumentsStore, PartnerIncomeTaxReadingClock],
  templateUrl: './partner-income-tax.component.html',
  styleUrl: './partner-income-tax.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerIncomeTaxComponent {
  protected readonly state = inject(PartnerIncomeTaxStateService);
  protected readonly sections = inject(PartnerIncomeTaxSectionState);
  protected readonly documents = inject(DataRoomDocumentsStore);
  private readonly permissions = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  protected readonly parametrizationRoute = PARAMETRIZATION_ROUTE;

  /** Sem `DATAROOM_VIEW` o link vira texto: a rota do Data Room recusaria. */
  protected readonly canOpenDataRoom = toSignal(
    this.permissions.hasPermission(Permissions.DataRoom.VIEW),
    { initialValue: false }
  );

  protected readonly policyDefined = computed(() => this.state.overview()?.policyDefined ?? false);

  protected readonly folderLink = computed(() => {
    const overview = this.state.overview();
    if (!overview) return null;
    return buildFolderLink(overview.companyId, overview.irpfFolderId, overview.partnerFolderId);
  });

  /** Dívidas por sócio (do comparativo do consolidado), para o card quando não há política de garantias. */
  protected readonly debtsByPerson = computed(() => {
    const rows = this.state.overview()?.consolidated?.comparison ?? [];
    return Object.fromEntries(rows.map(row => [row.personId, row.debts])) as Record<string, number>;
  });

  /** Rodada de leitura em andamento: "Nesta leitura: 1 de 4 documentos lidos", detalhe e barra (card 3772). */
  protected readonly readingProgress = computed(() => {
    const progress = this.state.overview()?.readingProgress;
    return progress ? describeReadingProgress(progress) : null;
  });

  /**
   * Aviso de documentos em leitura sem rodada (leitura pedida antes do card 3772, sem data do pedido): mantém o texto
   * antigo para a aba não ficar muda.
   */
  protected readonly processingText = computed(() => {
    if (this.readingProgress()) return null;
    const items =
      this.state.overview()?.processing.filter(item => item.status === 'processing') ?? [];
    if (!items.length) return null;
    const what =
      items.length === 1 ? `o documento ${items[0].fileName}` : `${items.length} documentos`;
    return `Lendo ${what}. A aba se atualiza sozinha quando a leitura terminar.`;
  });

  /** Aviso de documentos que a leitura não conseguiu ler, com o que fazer. */
  protected readonly failedText = computed(() => {
    const items = this.state.overview()?.processing.filter(item => item.status === 'failed') ?? [];
    if (!items.length) return null;
    const first = items[0];
    const detail = first.message ? ` (${first.message})` : '';
    const what =
      items.length === 1
        ? `o documento ${first.fileName}${detail}`
        : `${items.length} documentos, entre eles ${first.fileName}${detail}`;
    const action = this.state.canUpdate()
      ? 'e use Ler de novo.'
      : 'e peça a quem cuida da análise para ler de novo.';
    return `Não conseguimos ler ${what}. Confira se o arquivo é a declaração completa em PDF legível ${action}`;
  });

  /**
   * "IRPF 2024 a 2026 · saldos de 2022 a 2025": exercícios das declarações lidas do sócio aberto e anos dos saldos
   * da janela. No consolidado não há declarações no DTO: só os anos em que TODOS os sócios têm saldo, os mesmos do
   * total do comparativo (ano com sócio faltando não é base do grupo).
   */
  protected readonly baseRange = computed(() => {
    const selection = this.state.selection();
    if (selection?.kind === 'partner') {
      const partner = this.state.partner();
      const exercises =
        partner?.returns
          .filter(item => item.status === 'extracted')
          .map(item => item.exerciseYear) ?? [];
      return describeBaseRange(exercises, partner?.window ?? []);
    }
    // Anos comparáveis do grupo, como o backend os define (total por ano não nulo).
    const totals = this.state.overview()?.consolidated?.netWorthTotalByYear ?? [];
    return describeBaseRange(
      [],
      totals.filter(item => item.value != null).map(item => item.year)
    );
  });

  protected readonly isEmpty = computed(() => {
    const overview = this.state.overview();
    return !!overview && overview.partners.length === 0;
  });

  /** Botão do estado vazio: navega pelo router (o `app-button` não leva a diretiva `routerLink` aqui). */
  protected openFolder(link: PartnerIncomeTaxFolderLink): void {
    this.router.navigate(link.route, { queryParams: link.queryParams ?? undefined });
  }

  /**
   * "Ver" da linha de contexto: abre a aba interna Documentos e rola até ela. Só trocar a aba, lá embaixo, não mostrava
   * nada a quem clicou no topo da página (revisão com o Bruno, 29/10/2026).
   */
  protected openDocuments(): void {
    this.sections.select('documents');
    afterNextRender(
      () =>
        this.host.nativeElement
          .querySelector('app-pit-section-tabs')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      { injector: this.injector }
    );
  }

  protected openPartnerFromConsolidated(personId: string): void {
    this.state.selectPartner(personId);
  }
}
